import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { creditoAPI, cepAPI, formatarErroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import { CreditCard, Loader2, Search, History } from 'lucide-react';
import { ConectorAviso } from '@/components/ConectorAviso';
import { CreditoResultado, CreditoBadge, fmtDoc, fmtDataHora } from './CreditoResultado';

const soDigitos = (v) => (v || '').replace(/\D/g, '');
const FORM_INICIAL = { tipo_documento: 'CPF', cpf_cnpj: '', nome: '', data_nascimento: '', cep: '', cidade: '', uf: '', tipo_venda: 'PROSPECT', numero_contrato: '', plano_principal: 'TV' };

const Toggle = ({ opcoes, valor, onChange, testid }) => (
  <div className="inline-flex rounded-lg overflow-hidden border" data-testid={testid}>
    {opcoes.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v.toLowerCase()}`}
        className={`px-3 py-1.5 text-xs font-semibold transition-colors ${valor === v ? 'bg-purple-600 text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/20'}`}>
        {label}
      </button>
    ))}
  </div>
);

const HistoricoCredito = ({ historico, isAdmin, onAbrir }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" /> Últimas checagens</CardTitle>
      <CardDescription>{isAdmin ? 'Todas as checagens recentes.' : 'Suas checagens recentes.'}</CardDescription>
    </CardHeader>
    <CardContent>
      {historico.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="credito-historico-vazio">Nenhuma checagem ainda.</p>
      ) : (
        <div className="divide-y rounded-lg border" data-testid="credito-historico">
          {historico.map((h) => (
            <button key={h.id} type="button" onClick={() => onAbrir(h)} className="w-full text-left flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-purple-50/60 dark:hover:bg-purple-900/10" data-testid={`credito-hist-${h.id}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{h.nome}</p>
                <p className="text-[11px] text-gray-500 truncate">{fmtDoc(h.cpf_cnpj)} • {h.cidade}/{h.uf} • {fmtDataHora(h.respondida_em || h.criado_em)}{isAdmin ? ` • ${h.criado_por}` : ''}</p>
              </div>
              <CreditoBadge resultado={h.resultado} mensagem={h.mensagem} className="shrink-0" />
            </button>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

export const CreditoConsulta = ({ onResultado }) => {
  const { user } = useAuth();
  const veTudo = ['ADMIN', 'BKO', 'BKO_MAILING', 'GERENTE'].includes(user?.perfil);
  const [form, setForm] = useState(FORM_INICIAL);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [consultando, setConsultando] = useState(false);
  const [resultado, setResultadoState] = useState(null);
  const setResultado = (r) => { setResultadoState(r); if (r) onResultado?.(r); };
  const [aviso, setAviso] = useState(null);
  const [historico, setHistorico] = useState([]);

  const carregarHistorico = useCallback(async () => {
    try { setHistorico((await creditoAPI.historico()).data?.consultas || []); } catch (e) { /* silencioso */ }
  }, []);
  useEffect(() => { carregarHistorico(); }, [carregarHistorico]);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const tamanhoDoc = form.tipo_documento === 'CPF' ? 11 : 14;

  const buscarCep = async (cepRaw) => {
    const cep = soDigitos(cepRaw);
    if (cep.length !== 8) return;
    setBuscandoCep(true);
    try {
      const d = (await cepAPI.buscar(cep)).data || {};
      setForm((f) => ({ ...f, cidade: (d.localidade || '').toUpperCase(), uf: d.uf || '' }));
    } catch (e) {
      toast.error('CEP não encontrado');
    } finally {
      setBuscandoCep(false);
    }
  };

  const consultar = async (forcar = false) => {
    if (soDigitos(form.cpf_cnpj).length !== tamanhoDoc) { toast.error(`Informe um ${form.tipo_documento} válido`); return; }
    if (form.nome.trim().length < 3) { toast.error('Informe o nome completo'); return; }
    if (form.tipo_documento === 'CPF' && !form.data_nascimento) { toast.error('Informe a data de nascimento'); return; }
    if (soDigitos(form.cep).length !== 8) { toast.error('Informe um CEP válido'); return; }
    if (!form.cidade || form.uf.length !== 2) { toast.error('Cidade/UF não identificados — confira o CEP'); return; }
    if (form.tipo_venda === 'BASE' && !soDigitos(form.numero_contrato)) { toast.error('Informe o número do contrato do cliente'); return; }
    setConsultando(true); setAviso(null);
    if (!forcar) setResultado(null);
    try {
      const res = (await creditoAPI.consultar({ ...form, cpf_cnpj: soDigitos(form.cpf_cnpj), cep: soDigitos(form.cep), forcar, origem: 'tela' })).data;
      if (res.status === 'ok') setResultado(res); else { setAviso(res); if (!forcar) setResultado(null); }
      carregarHistorico();
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setConsultando(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-5" data-testid="credito-tab">
      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><CreditCard className="w-4 h-4" /> Checagem de crédito</CardTitle>
          <CardDescription>Mesma análise do portal da Conexão Claro: informe os dados do cliente e o endereço de instalação.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Toggle testid="credito-tipo-doc" valor={form.tipo_documento} onChange={(v) => setForm((f) => ({ ...f, tipo_documento: v, cpf_cnpj: '', data_nascimento: '' }))} opcoes={[['CPF', 'CPF (Residencial)'], ['CNPJ', 'CNPJ (Empresarial)']]} />
            <Toggle testid="credito-tipo-venda" valor={form.tipo_venda} onChange={(v) => setForm((f) => ({ ...f, tipo_venda: v }))} opcoes={[['PROSPECT', 'Novo cliente'], ['BASE', 'Já é cliente (Base)']]} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{form.tipo_documento}</Label>
              <Input value={fmtDoc(form.cpf_cnpj)} onChange={(e) => setForm((f) => ({ ...f, cpf_cnpj: soDigitos(e.target.value).slice(0, tamanhoDoc) }))} placeholder={form.tipo_documento === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'} inputMode="numeric" className="mt-1 font-mono" data-testid="credito-doc-input" />
            </div>
            <div>
              <Label className="text-xs">{form.tipo_documento === 'CPF' ? 'Nome completo' : 'Razão social / Nome'}</Label>
              <Input value={form.nome} onChange={set('nome')} className="mt-1 uppercase" data-testid="credito-nome-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {form.tipo_documento === 'CPF' && (
              <div>
                <Label className="text-xs">Data de nascimento</Label>
                <Input type="date" value={form.data_nascimento} onChange={set('data_nascimento')} className="mt-1" data-testid="credito-nascimento-input" />
              </div>
            )}
            <div>
              <Label className="text-xs">CEP de instalação</Label>
              <div className="relative mt-1">
                <Input value={form.cep} onChange={set('cep')} onBlur={(e) => buscarCep(e.target.value)} placeholder="00000-000" inputMode="numeric" data-testid="credito-cep-input" />
                {buscandoCep && <Loader2 className="w-4 h-4 animate-spin absolute right-2 top-2.5 text-gray-400" />}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label className="text-xs">Cidade</Label><Input value={form.cidade} onChange={set('cidade')} className="mt-1 uppercase" data-testid="credito-cidade-input" /></div>
            <div><Label className="text-xs">UF</Label><Input value={form.uf} onChange={(e) => setForm((f) => ({ ...f, uf: e.target.value.toUpperCase().slice(0, 2) }))} maxLength={2} className="mt-1 uppercase" data-testid="credito-uf-input" /></div>
          </div>
          {form.tipo_venda === 'BASE' && (
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-dashed p-3" data-testid="credito-base-box">
              <div><Label className="text-xs">Nº do contrato atual</Label><Input value={form.numero_contrato} onChange={(e) => setForm((f) => ({ ...f, numero_contrato: soDigitos(e.target.value) }))} inputMode="numeric" className="mt-1 font-mono" data-testid="credito-contrato-input" /></div>
              <div>
                <Label className="text-xs">Plano principal</Label>
                <Select value={form.plano_principal} onValueChange={(v) => setForm((f) => ({ ...f, plano_principal: v }))}>
                  <SelectTrigger className="mt-1" data-testid="credito-plano-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TV">TV</SelectItem><SelectItem value="INTERNET">Internet</SelectItem><SelectItem value="FONE">Fone</SelectItem><SelectItem value="MOVEL">Móvel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <Button onClick={() => consultar(false)} disabled={consultando} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="credito-consultar-button">
            {consultando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} {consultando ? 'Consultando a Claro…' : 'Checar crédito'}
          </Button>
          <ConectorAviso r={aviso} testid="credito-aviso" />
          {resultado && <CreditoResultado r={resultado} onNovamente={() => consultar(true)} consultando={consultando} />}
        </CardContent>
      </Card>
      <div className="lg:col-span-2">
        <HistoricoCredito historico={historico} isAdmin={veTudo} onAbrir={(h) => { setResultado(h); setAviso(null); }} />
      </div>
    </div>
  );
};
